import log from 'loglevel';
import { getTextOfJSDocComment } from 'typescript';
import { InitOptions, State, Source, QualityLevel, AudioTrack, VideoTrack, TextTrack, Control, QualityLevelKind, QualityLevelCurrentIndex, QualityLevels, Autoplay, NotificationAction, Responsive, ToggleControlsChangedEvents } from '../../../types/player';
import { Error, ErrorMessage } from '../../../types/error';
import ProviderInterface from '../../ProviderInterface';
import { PlayerEvents } from '../../PlayerEvents';
import Sdk from '../../Sdk';
import { Options } from '../../optionsManager/Options';
import UserAgent from '../../../utils/UserAgent';
import { has } from 'lodash';
import { t } from 'i18next';

class Html5 implements ProviderInterface {
  videoElement: HTMLVideoElement;
  element: HTMLElement;
  _events: PlayerEvents;
  currentState: State;
  options: Options;

  qualityLevels: QualityLevels = {
    audio: [],
    video: [],
  };
  qualityLevelCurrentIndex: QualityLevelCurrentIndex = {
    audio: -1,
    video: -1,
  };
  audioTracks: AudioTrack[] = [];
  videoTracks: VideoTrack[] = [];
  textTracks: TextTrack[] = [];

  currentAudioTrack = -1;
  currentVideoTrack = -1;
  currentTextTrack = -1;
  _isLive = false;
  _isDvr = false;
  _isDvrIsLive = false;
  _isLoop: boolean = false;
  _isStarted = false;

  _hasCustomDuration: boolean = false;
  _customDuration: number = -1;
  _customStartTime: number = -1;
  _customEndTime: number = -1;
  _needSeekBeforeReady: boolean = false;

  _readySended = false;

  pipStatus = false;
  fullScreenStatus = false;
  isLooping = false;
  controlsStatus: ToggleControlsChangedEvents = ToggleControlsChangedEvents.HIDE;
  _isDestroyed = false;

  //what is this?
  //private boundVolumeChange: any;

  constructor(element: HTMLElement, options: Options, events: PlayerEvents) {
    log.debug('Html5 init');
    this.options = options;
    this.element = element;
    this._events = events;
    this._isLoop = options.isLoop() || false;

    this.videoElement = document.createElement('video');
    element.appendChild(this.videoElement);
    this.videoElement.setAttribute('playsinline', '');
    this.videoElement.setAttribute('crossorigin', 'anonymous');
    if (options.isBackground()) {
      this.videoElement.setAttribute('disablePictureInPicture', '');
    }
    //this.videoElement.style.width = '300px'; //for test
    this.currentState = State.IDLE;
    this.listeners();
    //this.addSource(options.source);

    this.initAutoplay();
  }

  processTextTracks() {
    let textTracks = (this.options.getSource().textTracks as TextTrack[]) || [];
    if (!textTracks || textTracks.length === 0) return;
    for (let i = 0; i < textTracks.length; i++) {
      let index = this.addTextTrack(textTracks[i]);
      if (textTracks[i].default) {
        this.setCurrentTextTrack(index);
      }
    }
  }

  processCustomDuration() {
    let duration = this.getDuration();
    let hasCustomDuration = false;
    let hasStartTime = false;
    let hasEndTime = false;
    if (this.options.getSource().startTime) {
      this._customStartTime = this.options.getSource().startTime as number;
      hasStartTime = true;
      hasCustomDuration = true;
    }
    if (this.options.getSource().endTime) {
      this._customEndTime = this.options.getSource().endTime as number;
      hasEndTime = true;
      hasCustomDuration = true;
    }
    if (hasCustomDuration) {
      this._hasCustomDuration = true;
      if (hasStartTime && hasEndTime) {
        this._customDuration = this._customEndTime - this._customStartTime;
      } else if (hasStartTime && !hasEndTime) {
        this._customDuration = duration - this._customStartTime;
      } else if (!hasStartTime && hasEndTime) {
        this._customDuration = duration - this._customEndTime;
      }
    }
  }

  init(src: string) {
    this.videoElement.src = src;
  }

  reloadSource(src: string) {
    this.videoElement.src = src;
  }

  listeners() {
    this.videoElement.addEventListener('abort', this.onAbort.bind(this));
    this.videoElement.addEventListener('canplay', this.onCanPlay.bind(this));
    this.videoElement.addEventListener('canplaythrough', this.onCanPlayThrough.bind(this));
    this.videoElement.addEventListener('durationchange', this.onDurationChange.bind(this));
    this.videoElement.addEventListener('emptied', this.onEmptied.bind(this));
    this.videoElement.addEventListener('encrypted', this.onEncrypted.bind(this));
    this.videoElement.addEventListener('error', this.onError.bind(this));
    this.videoElement.addEventListener('loadeddata', this.onLoadedData.bind(this));
    this.videoElement.addEventListener('loadedmetadata', this.onLoadedMetadata.bind(this));
    this.videoElement.addEventListener('loadstart', this.onLoadStart.bind(this));
    this.videoElement.addEventListener('pause', this.onPause.bind(this));
    this.videoElement.addEventListener('play', this.onPlay.bind(this));
    this.videoElement.addEventListener('playing', this.onPlaying.bind(this));
    this.videoElement.addEventListener('progress', this.onProgress.bind(this));
    this.videoElement.addEventListener('ratechange', this.onRateChange.bind(this));
    this.videoElement.addEventListener('seeked', this.onSeeked.bind(this));
    this.videoElement.addEventListener('seeking', this.onSeeking.bind(this));
    this.videoElement.addEventListener('ended', this.onEnded.bind(this));
    this.videoElement.addEventListener('stalled', this.onStalled.bind(this));
    this.videoElement.addEventListener('suspend', this.onSuspend.bind(this));
    this.videoElement.addEventListener('timeupdate', this.onTimeUpdate.bind(this));
    this.videoElement.addEventListener('volumechange', this.onVolumeChange.bind(this));
    this.videoElement.addEventListener('waiting', this.onWaiting.bind(this));

    this.videoElement.addEventListener('enterpictureinpicture', this.onEnterPip.bind(this));
    this.videoElement.addEventListener('leavepictureinpicture', this.onLeavePip.bind(this));

    this.videoElement.addEventListener('webkitplaybacktargetavailabilitychanged', this.onAirPlayAvailabilityChanged.bind(this));

    document.addEventListener('fullscreenchange', this.handleFullScreenChange.bind(this));
  }

  isLive(): boolean {
    return this._isLive;
  }

  isDvr(): boolean {
    return this._isDvr;
  }

  isDvrIsLive(): boolean {
    return this._isDvrIsLive;
  }

  onAbort() {
    log.debug('Html5 abort');
  }

  onCanPlayThrough() {
    log.debug('Html5 can play through');
  }

  onDurationChange() {
    //log.debug('Html5 duration change');
  }

  onEmptied() {
    log.debug('Html5 emptied');
  }

  onEncrypted() {
    log.debug('Html5 encrypted');
  }

  onEnded() {
    if (this.isLoop()) {
      this.isLooping = true;
      this.seek(0);
      return;
    }
    log.debug('Html5 ended');
    const oldState = this.currentState;
    this.currentState = State.ENDED;
    this._events.stateChanged(oldState, this.currentState);
    this._events.end();
  }

  onError(event: Event) {
    log.debug('Html5 error');
    const target = event.target as HTMLVideoElement;
    const error = target.error as Error;
    this._events.error({
      code: error.code,
      message: ErrorMessage.codes[error.code].message,
    });
  }

  onFatalError() {    
    const oldState = this.currentState;
    this.currentState = State.ERROR;
    this._events.stateChanged(oldState, this.currentState);
  }

  onLoadedData() {
    log.debug('Html5 loaded data');
  }

  onLoadedMetadata() {
    log.debug('Html5 loaded metadata');
    if (this.videoElement.duration === Infinity) this._isLive = true;
  }

  onLoadStart() {
    log.debug('Html5 load start');
  }

  onPlay() {
    log.debug('Html5 play');
    this._events.play();
    this._isStarted = true;
  }

  isStarted(): boolean {
    return this._isStarted;
  }

  onProgress() {
    const bufferedEnd = this.videoElement.buffered.length > 0 ? this.videoElement.buffered.end(this.videoElement.buffered.length - 1) : 0;
    const duration = this.getDuration();
    const percentLoaded = (bufferedEnd / duration) * 100;
    this._events.progress(percentLoaded);
  }

  onRateChange() {
    log.debug('Html5 rate change');
  }

  onStalled() {
    log.debug('Html5 stalled');
  }

  onSuspend() {
    log.debug('Html5 suspend');
  }

  onWaiting() {
    log.debug('Html5 waiting');
    const oldState = this.currentState;
    this.currentState = State.LOADING;
    this._events.stateChanged(oldState, this.currentState);
  }

  onCanPlay() {
    log.debug('autoplay', 'Html5 can play');
    log.debug('Html5 can play', 'ready');

    if (!this._readySended) {
      if (!this._needSeekBeforeReady) {
        this.processCustomDuration();
        if (this._hasCustomDuration && this._customStartTime > 0 && !this._needSeekBeforeReady) {
          this._needSeekBeforeReady = true;
          this.videoElement.currentTime = this._customStartTime;
        }
      } else this._needSeekBeforeReady = false;
    }

    if (!this._readySended && !this._needSeekBeforeReady) {
      this._readySended = true;
      this.processTextTracks();
      if (this.options.hasAudio() === false) this.videoElement.muted = true;
      this.checkAutoplay();
      this._events.isReady();
    }
  }

  onPause() {
    log.debug('Html5 pause');
    const oldState = this.currentState;
    this.currentState = State.PAUSED;
    this._events.stateChanged(oldState, this.currentState);
    this._events.pause();
  }

  onPlaying() {
    log.debug('Html5 playing');
    const oldState = this.currentState;
    this.currentState = State.PLAYING;
    this._events.stateChanged(oldState, this.currentState);
  }

  onSeeked() {
    log.debug('Html5 seeked ', this.videoElement.currentTime);
    this._events.seeked();
    if (this.isLoop() && this.videoElement.currentTime == 0 && this.isLooping) {
      this.play();
      this.isLooping = false;
    }
  }

  onSeeking() {
    log.debug('Html5 seeking');
    this._events.seeking();
  }

  getCurrentTime(): number {
    if (this._hasCustomDuration && this._customStartTime > 0) return this.videoElement.currentTime - this._customStartTime;
    return this.videoElement.currentTime;
  }

  onTimeUpdate() {
    const currentTimeInPercentage = (this.getCurrentTime() / this.getDuration()) * 100;
    if (this._hasCustomDuration && this.videoElement.currentTime >= this._customEndTime) {
      this.pause();
      this.seek(0);
      this.onEnded();
      //TODO manage here the re-play button à la jwplayer
    }
    this._events.currentTime(this.getCurrentTime(), this.getDuration(), currentTimeInPercentage);
  }

  onVolumeChange() {
    log.debug('Html5 volume change', typeof this._events.volumeChanged, this.videoElement.volume, this.videoElement.muted);
    log.debug(this._events.volumeChanged);

    this._events.volumeChanged(this.videoElement.volume);
    this._events.muted(this.videoElement.muted);
  }

  seek(time: number) {
    log.debug('Html5 seek', time);
    if (this._hasCustomDuration && this._customStartTime > 0) time = time + this._customStartTime;
    this.videoElement.currentTime = time;
  }

  play() {
    log.debug('Html5 player');
    this.videoElement.play();
  }

  pause() {
    log.debug('Html5 pause');
    this.videoElement.pause();
  }

  isMuted(): boolean {
    return this.videoElement.muted;
  }

  toggleMute() {
    const volumeMuted = !this.isMuted();
    this.videoElement.muted = volumeMuted;

    if (!volumeMuted) {
      let volume = this.getVolume();
      if (volume === 0) volume = 0.25;
      this.setVolume(volume);
    }
    this._events.muted(volumeMuted);
  }

  getVolume(): number {
    return this.videoElement.volume;
  }

  setVolume(volume: number): void {
    if (this.isMuted()) this.videoElement.muted = false;
    let newVolume = parseFloat(volume.toFixed(2));
    this.videoElement.volume = newVolume;
    if (newVolume === 0) {
      this.videoElement.muted = true;
      this._events.muted(true);
    }
  }

  getDuration(): number {
    if (this._hasCustomDuration) return this._customDuration;
    return this.videoElement.duration;
  }

  setLoop(loop: boolean): void {
    this._isLoop = loop;
  }

  isLoop(): boolean {
    return this._isLoop;
  }

  hasQualityLevels(kind: QualityLevelKind): boolean {
    return this.qualityLevels[kind].length > 1;
  }

  setCurrentQualityLevel(index: number, kind: QualityLevelKind): QualityLevel {
    this.qualityLevelCurrentIndex[kind] = index;
    return this.qualityLevels[kind] ? [index] : (null as any);
  }

  getQualityLevels(kind: QualityLevelKind): QualityLevel[] {
    return this.qualityLevels[kind];
  }

  getCurrentQualityLevel(kind: QualityLevelKind): QualityLevel {
    return this.getQualityLevels(kind)[this.qualityLevelCurrentIndex[kind]];
  }

  isAutoQualityLevel(kind: QualityLevelKind): boolean {
    return false;
  }

  isAutoQualityLevelAvailable(kind: QualityLevelKind): boolean {
    return false;
  }

  hasAudioTracks(): boolean {
    return this.audioTracks.length > 1;
  }

  setCurrentAudioTrack(index: number): AudioTrack {
    this.currentAudioTrack = index;
    return this.audioTracks[this.currentAudioTrack];
    //this.videoElement.audioTracks[this.currentAudioTrack].enabled = true;
  }

  getAudioTracks(): AudioTrack[] {
    return this.audioTracks;
  }

  getCurrentAudioTrack(): AudioTrack {
    return this.audioTracks[this.currentAudioTrack];
  }

  hasVideoTracks(): boolean {
    return this.videoTracks.length > 1;
  }

  setCurrentVideoTrack(index: number): VideoTrack {
    this.currentVideoTrack = index;
    return this.videoTracks[this.currentVideoTrack];
  }

  getVideoTracks(): VideoTrack[] {
    return this.videoTracks;
  }

  getCurrentVideoTrack(): VideoTrack {
    return this.videoTracks[this.currentVideoTrack];
  }

  hasTextTracks(): boolean {
    return this.textTracks.length > 0;
  }

  setCurrentTextTrack(index: number): TextTrack {
    if (this.hasTextTracks() === false) return null as any;
    if (index === -1) {
      this.videoElement.textTracks[this.currentTextTrack].mode = 'disabled';
      this.currentTextTrack = index;
    } else {
      if(this.currentTextTrack !== -1)
        this.videoElement.textTracks[this.currentTextTrack].mode = 'disabled';
      this.currentTextTrack = index;
      if (!this.videoElement.textTracks[this.currentTextTrack]) return null as any;
      this.videoElement.textTracks[this.currentTextTrack].mode = 'showing';
    }
    this._events.textTrackChanged(this.textTracks[this.currentTextTrack], index);
    return this.textTracks[this.currentTextTrack];
  }

  getTextTracks(): TextTrack[] {
    return this.textTracks;
  }

  getCurrentTextTrack(): TextTrack {
    return this.textTracks[this.currentTextTrack];
  }

  addTextTrack(textTrack: TextTrack): number {
    let index = this.textTracks.length;
    textTrack.index = index;
    this.textTracks.push(textTrack);
    var track = document.createElement('track');
    track.kind = textTrack.kind;
    track.label = textTrack.label;
    track.srclang = textTrack.srclang;
    track.src = textTrack.src;  
    this.videoElement.appendChild(track);
    textTrack.trackElement = track as HTMLTrackElement;
    log.debug('addTextTrack', textTrack, index);
    return index;
  }

  hasPlaybackRates(): boolean {
    let playbackRates = this.options.getControls().getSettings().playbackRates;
    playbackRates = playbackRates || [];
    return playbackRates.length > 0;
  }

  getPlaybackRates(): number[] {
    let playbackRates = this.options.getControls().getSettings().playbackRates;
    playbackRates = playbackRates || [];
    return playbackRates;
  }

  getCurrentPlaybackRate(): number {
    return this.videoElement.playbackRate;
  }

  setCurrentPlaybackRate(rate: number): void {
    this.videoElement.playbackRate = rate;
    this._events.playbackRateChanged(rate);
  }

  onEnterPip() {
    this.pipStatus = true;
    this._events.pipStatusChanged(true);
  }

  onLeavePip() {
    this.pipStatus = false;
    this._events.pipStatusChanged(false);
  }

  hasPip() {
    return this.videoElement.requestPictureInPicture !== undefined;
  }

  togglePip() {
    if (this.videoElement.requestPictureInPicture) {
      !this.pipStatus ? this.videoElement.requestPictureInPicture() : document.exitPictureInPicture();
    }
  }

  onAirPlayAvailabilityChanged(event: any) {
    if (event.availability === 'available') {
      this._events.airPlayAvailabilityChanged(true);
    } else {
      this._events.airPlayAvailabilityChanged(false);
    }
  }

  hasAirPlay(): boolean {
    if (!window.WebKitPlaybackTargetAvailabilityEvent) return false;
    return true;
  }

  showAirPlayTargetPicker() {
    /*@ts-ignore */
    this.videoElement.webkitShowPlaybackTargetPicker();
  }

  handleFullScreenChange() {
    this.fullScreenStatus = !this.fullScreenStatus;
    this._events.fullscreenChanged(this.fullScreenStatus);
  }
  isInFullScreen() {
    return (document.fullscreenElement && document.fullscreenElement !== null) || (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) || (document.mozFullScreenElement && document.mozFullScreenElement !== null) || (document.msFullscreenElement && document.msFullscreenElement !== null);
  }

  toggleFullScreen(userAgent: UserAgent) {
    /* @ts-ignore */
    if (userAgent.getOS() == 'iOS' && this.videoElement.webkitSupportsFullscreen) {
      /* @ts-ignore */
      this.videoElement.webkitEnterFullscreen();
      return;
    }

    const isInFullScreen = this.isInFullScreen();
    if (!isInFullScreen) {
      if (this.element) {
        const parent = this.element.parentNode;
        if (parent && parent instanceof HTMLElement) {
          const el: HTMLElement = parent;

          if (el.requestFullscreen) {
            el.requestFullscreen();
          } else if (el.mozRequestFullScreen) {
            el.mozRequestFullScreen();
          } else if (el.webkitRequestFullScreen) {
            el.webkitRequestFullScreen();
          } else if (el.msRequestFullscreen) {
            el.msRequestFullscreen();
          }
        }
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      return false;
    }
  }

  toggleControls() {
    this._events.toggleControls();
  }

  initAutoplay() {
    let autoplay = this.options.getAutoplay();
    if (this.options.getMute()) {
      this.videoElement.muted = true;
    }

    if (autoplay == Autoplay.NO) {
      setTimeout(() => {
        this.onPause();
      }, 100); //a bit strange
      return;
    }

    this.videoElement.autoplay = true;
  }

  checkAutoplay() {
    let autoplay = this.options.getAutoplay();
    log.debug('Autoplay test', autoplay);
    if (autoplay === Autoplay.NO) return;
    this.videoElement.play().catch((err) => {
      log.debug('Autoplay failed', err);
      if (autoplay === Autoplay.VIEWABLE) {
        log.debug('Autoplay true and set muted');
        this.videoElement.muted = true;
        this.videoElement.play().catch((errMuted) => {
          log.debug('Autoplay failed even after muting', errMuted);
          this.onPause();
        });
      } else {
        this.onPause();
      }
    });
  }

  notificationAction(action: NotificationAction) {
    this._events.notificationAction(action);
  }

  toggleControlMenu(control: string) {
    this._events.toggleControlMenu(control);
  }

  clickControl(control: string) {
    this._events.clickControl(control);
  }

  toggleControlsChanged(status: ToggleControlsChangedEvents) {
    if (this.controlsStatus == status) return;
    this.controlsStatus = status;
    this._events.toggleControlsChanged(status);
  }

  getControlsStatus(): ToggleControlsChangedEvents {
    return this.controlsStatus;
  }

  getLibrary(): any {
    return this.videoElement;
  }

  stop() {
    this.videoElement.pause();
    this.videoElement.src = '';
    this.videoElement.load();
  }

  destroy(): void {
    this._isDestroyed = true;
    this._readySended = false;
    this.stop();
    this.videoElement.remove();
  }
}

export { Html5 };
